import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Clock, AlertTriangle, XCircle, X } from 'lucide-react'

const TrialBanner = ({ currentUser }) => {
  const { t, i18n } = useTranslation()
  const [dismissed, setDismissed] = useState(false)

  if (dismissed) return null
  if (!currentUser) return null

  const { membership_status, trial_days_remaining } = currentUser

  // Paid users or admins don't see the banner
  if (membership_status === 'paid') return null

  const settingsUrl = `/settings?locale=${i18n.language}`

  // Trial ended (free user)
  if (membership_status === 'free') {
    return (
      <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-3">
          <XCircle className="text-red-400 flex-shrink-0" size={20} />
          <p className="text-red-400 font-bold text-sm flex-1">{t('trial_banner.ended')}</p>
          <a
            href={settingsUrl}
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-xs px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex-shrink-0"
          >
            {t('trial_banner.upgrade_cta')}
          </a>
        </div>
        <p className="text-gray-400 text-xs mt-2">{t('trial_banner.lost_features')}</p>
      </div>
    )
  }

  // Trial user — show different urgency levels
  if (membership_status === 'trial') {
    const days = trial_days_remaining

    // Last day
    if (days <= 1) {
      return (
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-4 mb-6 animate-pulse">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-red-400 flex-shrink-0" size={20} />
            <p className="text-red-400 font-bold text-sm flex-1">{t('trial_banner.last_day')}</p>
            <a
              href={settingsUrl}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-xs px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex-shrink-0"
            >
              {t('trial_banner.upgrade_cta')}
            </a>
          </div>
          <p className="text-gray-400 text-xs mt-2">{t('trial_banner.lost_features')}</p>
        </div>
      )
    }

    // 3 days or less
    if (days <= 3) {
      return (
        <div className="bg-orange-500/10 border border-orange-500/50 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-orange-400 flex-shrink-0" size={20} />
            <p className="text-orange-400 font-bold text-sm flex-1">
              {t('trial_banner.ending_soon')} — {t('trial_banner.days_remaining', { days })}
            </p>
            <a
              href={settingsUrl}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold text-xs px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex-shrink-0"
            >
              {t('trial_banner.upgrade_cta')}
            </a>
          </div>
          <p className="text-gray-400 text-xs mt-2">{t('trial_banner.keep_features')}</p>
        </div>
      )
    }

    // 14 days or less
    if (days <= 14) {
      return (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <Clock className="text-yellow-400 flex-shrink-0" size={20} />
            <p className="text-yellow-400 font-bold text-sm flex-1">
              {t('trial_banner.enjoying')} — {t('trial_banner.days_remaining', { days })}
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a
                href={settingsUrl}
                className="bg-[#2c2c2e] border border-gray-700 text-gray-300 font-bold text-xs px-3 py-2 rounded-lg hover:bg-[#3a3a3c] transition-colors"
              >
                {t('trial_banner.upgrade_cta')}
              </a>
              <button
                onClick={() => setDismissed(true)}
                className="text-gray-600 hover:text-gray-400 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <p className="text-gray-400 text-xs mt-2">{t('trial_banner.keep_features')}</p>
        </div>
      )
    }

    // More than 14 days — no banner (early in trial, let them enjoy)
    return null
  }

  return null
}

export default TrialBanner
